import React, { useEffect, useState } from 'react';
import { ShieldCheck, CheckCircle, Activity, CreditCard, RefreshCw, AlertTriangle, ChevronRight } from 'lucide-react';

export default function TermsOfService() {
  const [activeSection, setActiveSection] = useState('acceptance');

  const sections = [
    { id: 'acceptance', title: '1. Acceptance of Terms', icon: <CheckCircle size={18} /> },
    { id: 'use', title: '2. Use of Services', icon: <Activity size={18} /> },
    { id: 'bookings', title: '3. Bookings & Payments', icon: <CreditCard size={18} /> },
    { id: 'cancellations', title: '4. Cancellations & Refunds', icon: <RefreshCw size={18} /> },
    { id: 'liability', title: '5. Limitation of Liability', icon: <AlertTriangle size={18} /> }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-deep)',
      paddingTop: '140px',
      paddingBottom: '100px',
      color: 'var(--text-light)',
      fontFamily: 'Inter, sans-serif'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px'
      }}>
        {/* Header Area */}
        <div className="animate-slide-up" style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'var(--primary-gold-trans)',
            marginBottom: '24px',
            border: '1px solid var(--primary-gold-border)'
          }}>
            <ShieldCheck size={40} className="text-gold" />
          </div>
          <h1 className="font-serif text-gold" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', marginBottom: '16px', lineHeight: '1.2' }}>
            Terms of Service
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
            Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Main Layout: Sidebar + Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '40px',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          
          {/* Sidebar Navigation */}
          <div className="glass-card animate-slide-up" style={{
            flex: '1 1 300px',
            position: 'sticky',
            top: '120px',
            padding: '30px',
            animationDelay: '0.1s'
          }}>
            <h3 className="font-serif text-gold" style={{ fontSize: '1.25rem', marginBottom: '20px', borderBottom: '1px solid var(--primary-gold-border)', paddingBottom: '15px' }}>
              Contents
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sections.map((section) => (
                <li key={section.id}>
                  <button 
                    onClick={() => scrollToSection(section.id)}
                    style={{
                      background: activeSection === section.id ? 'var(--primary-gold-trans)' : 'transparent',
                      border: '1px solid',
                      borderColor: activeSection === section.id ? 'var(--primary-gold-border)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      color: activeSection === section.id ? 'var(--primary-gold)' : 'var(--text-muted)',
                      transition: 'var(--transition-fast)',
                      fontWeight: activeSection === section.id ? '600' : '500',
                      fontSize: '0.95rem'
                    }}
                    onMouseOver={(e) => {
                      if (activeSection !== section.id) {
                        e.currentTarget.style.backgroundColor = 'var(--bg-panel)';
                        e.currentTarget.style.color = 'var(--text-light)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (activeSection !== section.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-muted)';
                      }
                    }}
                  >
                    <span style={{ opacity: activeSection === section.id ? 1 : 0.7 }}>{section.icon}</span>
                    <span style={{ flex: 1 }}>{section.title}</span>
                    {activeSection === section.id && <ChevronRight size={16} />}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Content Area */}
          <div className="terms-section" style={{ flex: '3 1 600px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
            
            {/* 1. Acceptance of Terms */}
            <div id="acceptance" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.2s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <CheckCircle className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>1. Acceptance of Terms</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', margin: 0 }}>
                By accessing or using the services provided by <strong>Sunshine Travels</strong>, you agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions of this agreement, then you may not access the website or use any of our services. These Terms of Service apply to all users of the site, including without limitation users who are browsers, vendors, customers, merchants, and/or contributors of content.
              </p>
            </div>

            {/* 2. Use of Services */}
            <div id="use" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.3s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <Activity className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>2. Use of Services</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '20px' }}>
                You may use our services solely for your personal and non-commercial purposes. When using our platform, you agree to:
              </p>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                {[
                  'Provide accurate, current, and complete information during the registration and booking processes.',
                  'Maintain the security of your password and identification.',
                  'Accept responsibility for all activities that occur under your account or password.',
                  'Not engage in any activity that interferes with or disrupts our services or networks.'
                ].map((text, idx) => (
                  <li key={idx} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    color: 'var(--text-muted)',
                    lineHeight: '1.7'
                  }}>
                    <div style={{ marginTop: '6px', color: 'var(--primary-gold)' }}>
                      <ChevronRight size={16} />
                    </div>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 3. Bookings & Payments */}
            <div id="bookings" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.4s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <CreditCard className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>3. Bookings & Payments</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', margin: 0 }}>
                All bookings are subject to availability and acceptance by Sunshine Travels. We reserve the right to refuse or cancel any booking for any reason. Payments must be made in full at the time of booking unless otherwise stated. Prices are subject to change without notice, but changes will not affect bookings that have already been confirmed. We use secure third-party payment processors to ensure your payment information is protected.
              </p>
            </div>

            {/* 4. Cancellations & Refunds */}
            <div id="cancellations" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.5s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <RefreshCw className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>4. Cancellations & Refunds</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', margin: 0 }}>
                Cancellation policies vary depending on the specific retreat or service booked. Please review the specific cancellation policy provided at the time of booking. Generally, cancellations made more than 30 days prior to the start date are eligible for a full refund minus a processing fee. Cancellations made within 30 days may be subject to partial or no refund. Refunds will be processed to the original method of payment.
              </p>
            </div>

            {/* 5. Limitation of Liability */}
            <div id="liability" className="glass-card animate-slide-up" style={{ padding: '40px', animationDelay: '0.6s', scrollMarginTop: '120px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ padding: '10px', background: 'var(--primary-gold-trans)', borderRadius: '10px' }}>
                  <AlertTriangle className="text-gold" size={24} />
                </div>
                <h2 className="font-serif text-gold" style={{ fontSize: '1.75rem', margin: 0 }}>5. Limitation of Liability</h2>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', fontSize: '1.05rem', margin: 0 }}>
                To the fullest extent permitted by applicable law, Sunshine Travels shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from (a) your access to or use of or inability to access or use the services; (b) any conduct or content of any third party on the services; or (c) unauthorized access, use, or alteration of your transmissions or content.
              </p>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .terms-section .glass-card { padding: 20px 16px !important; }
        }
      `}</style>
    </div>
  );
}
